/**
 * Shared utility functions for name formatting and content safety moderation.
 */

/**
 * Formats a player's name based on their privacy preference.
 * - If showFullName is true or the viewer is the owner of the profile, shows the full name.
 * - Otherwise, formats "First Last" to "First L."
 * 
 * @param {string} fullName The full name stored in the database.
 * @param {boolean} showFullName True if the user elected to show their full name publicly.
 * @param {boolean} isOwnProfile True if the logged-in user is the owner of this profile.
 * @param {string} profileType The type of profile ('jugador' or 'equipo').
 * @returns {string} The formatted name.
 */
export const formatPlayerName = (fullName, showFullName, isOwnProfile = false, profileType = 'jugador') => {
  if (!fullName) return '';
  if (profileType !== 'jugador') return fullName; // Teams don't have this constraint
  if (showFullName) return fullName;
  
  // Format "First Last" to "First L."
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 1) return fullName;
  
  const firstName = parts[0];
  const lastName = parts[parts.length - 1];
  const lastInitial = lastName.charAt(0).toUpperCase();
  return `${firstName} ${lastInitial}.`;
};

/**
 * Checks if a name contains Spanish profanities, illegal terms, or inappropriate content.
 * Normalizes text to ignore accents, capitalization, and spaces.
 * 
 * @param {string} text The name text to validate.
 * @returns {boolean} True if the text contains inappropriate content, false otherwise.
 */
export const containsProfanity = (text) => {
  if (!text) return false;
  
  // Normalize: lowercase, remove accents/diacritics
  const normalized = text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); // removes accents
    
  // Curated list of Spanish profanities, insults, and inappropriate terms
  const forbiddenWords = [
    "mierda", "puto", "puta", "concha", "pija", "orto", "cagar", "cagado",
    "boludo", "pelotudo", "hijodeputa", "forro", "trolo", "trola", "choto", 
    "chota", "culiado", "culia", "coger", "pajero", "pajera", "paja",
    "violador", "nazi", "facha", "asesino", "droga", "cocaina"
  ];
  
  // Remove all internal spaces to capture attempts to bypass (e.g. "p u t o")
  const cleanText = normalized.replace(/\s+/g, "");
  
  for (const word of forbiddenWords) {
    if (word.length > 4) {
      // Substring check for longer words
      if (cleanText.includes(word)) return true;
    } else {
      // Exact word check for short words to prevent false positives (e.g. "paja" shouldn't block "pajarito")
      const words = normalized.split(/\s+/);
      if (words.includes(word)) return true;
    }
  }
  
  return false;
};
